import React, { useState, useEffect, useContext } from "react";
import useErrorHandler from "@beeldit/core/errors/useErrorHandler";
import index from "@beeldit/core/services";
import { Extra } from "../models/Extra"; // Asegúrate de que Extra esté bien definido
import { ConfigurationsContext } from "@main/contexts/ConfigurationsContext";

interface Option {
  label: string;
  value: any;
}

function CustomTableWidget(props: any) {
  const { value, onChange, formContext } = props;
  const [elementsData, setElementsData] = useState<{ elements: Extra[] }>({
    elements: [],
  });
  const [selectedExtras, setSelectedExtras] = useState<any[]>([]);
  const [inputsState, setInputsState] = useState<
    Record<number, { price: number; quantity: number; enabled: boolean }>
  >({});

  const errorHandler = useErrorHandler();
  const { element } = formContext;

  const { extras, setExtras } = useContext(ConfigurationsContext);

  useEffect(() => {
    // Verificar si ya hay valores en "extras"
    if (extras && extras.length > 0) {
      setElementsData({ elements: extras }); // Usar los valores existentes
      initializeInputsState(extras);
    } else {
      // Hacer la petición si no hay valores en "extras"
      index("extras")
        .then((response: any) => {
          setElementsData(response.data); // Guardar en el estado local
          setExtras(response.data.elements); // Guardar en el contexto global

          initializeInputsState(response.data.elements);
        })
        .catch((error: any) => {
          errorHandler(error, null);
        });
    }
  }, []);

  const initializeInputsState = (elements: Extra[]) => {
    // Inicializar el estado de los inputs con valores por defecto
    const initialState = elements.reduce(
      (acc: any, extra: Extra) => ({
        ...acc,
        [extra.id]: {
          price: getPrice(extra),
          quantity: getQuantity(extra), // Llamamos a getQuantity con el extra actual
          enabled: isEnabled(extra), // Llamamos a isEnabled con el extra actual
        },
      }),
      {}
    );
    setInputsState(initialState);
  };

  const isEnabled = (extra: Extra) => {
    // Buscamos en element un elemento con extra_id = extra.id
    return element.extras.some((e: any) => e.extra_id === extra.id);
  };

  const getQuantity = (extra: Extra) => {
    // Buscamos en element un elemento con extra_id = extra.id
    const elementExtra = element.extras.find((e: any) => e.extra_id === extra.id);
    return elementExtra ? elementExtra.quantity : 1;
  };

  const getPrice = (extra: Extra) => {
    // Buscamos en element un elemento con extra_id = extra.id
    const elementExtra = element.extras.find((e: any) => e.extra_id === extra.id);
    return elementExtra ? elementExtra.price : extra.price;
  };

  const handleCheckboxChange = (id: number) => {
    const newState = {
      ...inputsState,
      [id]: { ...inputsState[id], enabled: !inputsState[id].enabled },
    };
    setInputsState(newState);
  };

  const handleInputChange = (
    id: number,
    field: "price" | "quantity",
    value: number
  ) => {
    const newState = {
      ...inputsState,
      [id]: { ...inputsState[id], [field]: value },
    };
    setInputsState(newState);
  };

  const handleOnChange = () => {
    const selectedExtras = Object.entries(inputsState)
      .filter(([id, state]) => state.enabled)
      .map(([id, state]) => {
        // Busca el extra por ID y asegúrate de que existe
        const extra = elementsData.elements.find(
          (e: Extra) => e.id === parseInt(id)
        );

        if (!extra) {
          throw new Error(`Extra with id ${id} not found`);
        }

        return {
          extra_id: parseInt(id),
          price: state.price,
          quantity: state.quantity,
          type: extra.type, // Ahora accedemos a `type` de manera segura
        };
      });
    setSelectedExtras(selectedExtras);
    onChange(selectedExtras); // Actualizamos el form data
  };

  useEffect(() => {
    handleOnChange();
  }, [inputsState]);

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Seleccionar</th>
          <th>Nombre</th>
          <th>Precio</th>
          <th>Cantidad</th>
        </tr>
      </thead>
      <tbody>
        {elementsData.elements.map((extra: Extra) => (
          <tr key={extra.id}>
            <td>
              <input
                type="checkbox"
                checked={inputsState[extra.id]?.enabled || false}
                onChange={() => handleCheckboxChange(extra.id)}
              />
            </td>
            <td>{extra.name}</td>
            <td>
              <input
                type="number"
                min="0"
                step="0.01"
                value={inputsState[extra.id]?.price || 0}
                disabled={!inputsState[extra.id]?.enabled}
                onChange={(e) =>
                  handleInputChange(
                    extra.id,
                    "price",
                    parseFloat(e.target.value)
                  )
                }
              />
              {extra.type === 1 && <span> por día</span>}
            </td>
            <td>
              <input
                type="number"
                min="0"
                step="1"
                value={inputsState[extra.id]?.quantity || 0}
                disabled={!inputsState[extra.id]?.enabled}
                onChange={(e) =>
                  handleInputChange(
                    extra.id,
                    "quantity",
                    parseFloat(e.target.value)
                  )
                }
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ExtraSelector(props: any) {
  const { id, value, required, disabled, readonly, onChange, formContext } =
    props;
  return (
    <CustomTableWidget
      value={value}
      required={required}
      disabled={disabled}
      readonly={readonly}
      onChange={onChange}
      formContext={formContext}
    />
  );
}

export default ExtraSelector;
